<script setup lang="ts">
  import { computed, onMounted, ref } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { LayoutGrid, PenLine, TriangleAlert } from "lucide-vue-next";
  import OnePanePage from "@templates/OnePanePage.vue";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useCreativesDependencies } from "@composables/useCreativesDependencies";
  import { useCreativeSummary } from "@composables/useCreativeSummary";
  import type { MCampaignPlacement, MCreative } from "@/types";

  const ALL_CREATIVES = "all";
  const STATUS_BADGES = {
    missing: "Not built",
    outdated: "Outdated",
  } as const;

  const router = useRouter();

  const { currentMCampaign } = storeToRefs(useMCampaignStore());
  const { loadCreativeAssets } = useCreativesDependencies();
  const { visuals, localeLabel } = useCreativeSummary();

  const placement = ref<MCampaignPlacement>("feed");
  const creativeFilter = ref<string>(ALL_CREATIVES);
  const isLoading = ref(true);
  const hasLoadingError = ref(false);

  const pluralizeVisuals = (count: number) => `${count} ${count === 1 ? "visual" : "visuals"}`;

  const creatives = computed<MCreative[]>(() => currentMCampaign.value?.m_creatives ?? []);

  const sections = computed(() =>
    creatives.value
      .filter((creative) => creativeFilter.value === ALL_CREATIVES || creative.token === creativeFilter.value)
      .map((creative) => {
        const creativeVisuals = visuals(creative, placement.value);
        const placements = (["feed", "story"] as const).filter((key) => creative.placements?.[key]);
        const templateNames = [...new Set(creativeVisuals.map((visual) => visual.template.name))];

        return {
          creative,
          visuals: creativeVisuals,
          meta: [
            templateNames.join(", "),
            pluralizeVisuals(creativeVisuals.length),
            placements.map((key) => (key === "feed" ? "Feed" : "Story")).join(" + "),
            localeLabel(creative),
          ]
            .filter(Boolean)
            .join(" · "),
        };
      }),
  );

  const visualsCount = computed(() => sections.value.reduce((count, section) => count + section.visuals.length, 0));

  const editCreative = (creative: MCreative) =>
    router.push({
      name: "MCampaignCreatives",
      params: { token: currentMCampaign.value?.token },
      query: { creative: creative.token },
    });

  const loadAssets = async () => {
    isLoading.value = true;
    hasLoadingError.value = !(await loadCreativeAssets());
    isLoading.value = false;
  };

  onMounted(loadAssets);
</script>

<template>
  <OnePanePage data-cy="master-campaign-creatives-overview-page">
    <div class="flex flex-col gap-6">
      <div class="flex flex-wrap items-center gap-4 rounded-xl border bg-background px-4 py-3">
        <Tabs v-model="placement">
          <TabsList>
            <TabsTrigger value="feed">Feed</TabsTrigger>
            <TabsTrigger value="story">Story</TabsTrigger>
          </TabsList>
        </Tabs>
        <div class="h-6 w-px bg-border" />
        <div class="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            class="rounded-full"
            :variant="creativeFilter === ALL_CREATIVES ? 'secondary' : 'outline'"
            @click="creativeFilter = ALL_CREATIVES">
            All creatives
          </Button>
          <Button
            v-for="creative in creatives"
            :key="creative.token"
            size="sm"
            class="rounded-full"
            :variant="creativeFilter === creative.token ? 'secondary' : 'outline'"
            @click="creativeFilter = creative.token">
            {{ creative.name }}
          </Button>
        </div>
        <span
          v-if="!isLoading && !hasLoadingError"
          class="ml-auto text-xs text-muted-foreground">
          {{ pluralizeVisuals(visualsCount) }} · {{ placement === "feed" ? "Feed" : "Story" }}
        </span>
      </div>

      <LoadingState
        v-if="isLoading"
        label="Loading visuals..." />

      <EmptyState
        v-else-if="hasLoadingError"
        :icon="TriangleAlert"
        title="Visuals could not be loaded"
        description="The templates or the campaign assets failed to load.">
        <template #action>
          <Button
            variant="outline"
            size="sm"
            @click="loadAssets">
            Retry
          </Button>
        </template>
      </EmptyState>

      <EmptyState
        v-else-if="!creatives.length"
        :icon="LayoutGrid"
        title="No creative yet"
        description="Add creatives in the Creatives tab to see them here." />

      <template v-else>
        <section
          v-for="section in sections"
          :key="section.creative.token"
          class="flex flex-col gap-3"
          :data-cy="`creative-overview-${section.creative.token}`">
          <div class="flex items-start justify-between gap-4">
            <div class="flex min-w-0 flex-col gap-1">
              <div class="flex items-center gap-2">
                <h2 class="heading-title">{{ section.creative.name }}</h2>
                <span class="text-xs text-muted-foreground">{{ section.meta }}</span>
                <Badge
                  v-if="section.creative.status !== 'active'"
                  variant="neutral">
                  Inactive
                </Badge>
              </div>
              <p
                v-if="section.creative.titles?.[0]"
                class="truncate text-xs text-foreground-secondary">
                <span class="text-muted-foreground">Primary text:</span> {{ section.creative.titles[0] }}
              </p>
              <p
                v-if="section.creative.subtitles?.[0]"
                class="truncate text-xs text-foreground-secondary">
                <span class="text-muted-foreground">Catchphrase:</span> {{ section.creative.subtitles[0] }}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              @click="editCreative(section.creative)">
              <PenLine class="size-4" />
              Edit creative
            </Button>
          </div>

          <p
            v-if="!section.visuals.length"
            class="text-xs text-muted-foreground">
            No visual for this placement.
          </p>
          <div
            v-else
            class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
            <button
              v-for="visual in section.visuals"
              :key="visual.key"
              type="button"
              class="flex flex-col gap-1.5 text-left"
              @click="editCreative(section.creative)">
              <span
                class="relative block w-full overflow-hidden rounded-lg border bg-muted"
                :class="placement === 'feed' ? 'aspect-square' : 'aspect-9/16'">
                <video
                  v-if="section.creative.media_type === 'video'"
                  class="size-full object-cover"
                  :src="visual.url ? `${visual.url}#t=0.1` : undefined"
                  muted
                  preload="metadata" />
                <img
                  v-else
                  class="size-full object-cover"
                  :class="{ 'opacity-50': visual.status !== 'built' }"
                  :src="visual.url ?? undefined"
                  :alt="`${visual.template.name} ${visual.assetLabel}`"
                  loading="lazy" />
                <Badge
                  v-if="visual.status !== 'built' && section.creative.media_type === 'image'"
                  variant="warning"
                  class="absolute top-2 left-2">
                  {{ STATUS_BADGES[visual.status] }}
                </Badge>
              </span>
              <span class="flex min-w-0 gap-1.5 text-xs">
                <span class="shrink-0 font-semibold text-primary">{{ visual.template.name }}</span>
                <span class="truncate text-muted-foreground">{{ visual.assetLabel }}</span>
              </span>
            </button>
          </div>
        </section>
      </template>
    </div>
  </OnePanePage>
</template>
